import type { SharedPostMediaDto } from '@reelplicate/api-client/model';
import { useEffect, useState } from 'react';

export type MediaFiles = {
  /** Every file, once all have arrived; null until then or after a failure. */
  files: File[] | null;
  loaded: number;
  /** Bytes expected, when the server said. */
  total: number;
  failed: boolean;
};

/**
 * The shared post's files, fetched as soon as the page opens. iOS only opens
 * the share sheet when `navigator.share` is the first thing a tap does - an
 * `await` before it (like a fetch) loses the tap - so the files must already
 * be here when the button is pressed. Same-origin paths, so no auth or CORS.
 * Fetched once per `media` (the page keeps one post).
 */
export function useMediaFiles(media: SharedPostMediaDto[]): MediaFiles {
  const [state, setState] = useState<Omit<MediaFiles, 'total'>>({
    files: null,
    loaded: 0,
    failed: false,
  });
  const total = media.reduce((sum, m) => sum + (m.size ?? 0), 0);

  useEffect(() => {
    if (!media.length) return;
    const abort = new AbortController();
    const progress = media.map(() => 0);

    const fetchOne = async (item: SharedPostMediaDto, index: number) => {
      const response = await fetch(item.url, { signal: abort.signal });
      if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);
      const reader = response.body.getReader();
      const chunks: BlobPart[] = [];
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        progress[index] = (progress[index] ?? 0) + value.length;
        const loaded = progress.reduce((sum, n) => sum + n, 0);
        setState((s) => ({ ...s, loaded }));
      }
      const type = response.headers.get('content-type') ?? 'application/octet-stream';
      return new File(chunks, item.fileName, { type });
    };

    Promise.all(media.map(fetchOne))
      .then((files) => setState((s) => ({ ...s, files })))
      .catch(() => {
        if (!abort.signal.aborted) setState((s) => ({ ...s, failed: true }));
      });
    return () => abort.abort();
  }, [media]);

  return { ...state, total };
}
